// 录入照片：识别 → 自动裁成 3:4 人像 → 温和清理 → 存库

import { type Angle, type Photo, packAnalysis, put, uid } from "./db";
import { portraitCrop } from "./geometry";
import { autoClean, blobToCanvas, canvasToBlob, crop } from "./image";
import { analyze, estimateYaw, faceBox, type LoadProgress, loadVision } from "./vision";

export const BASE_MAX_W = 1200;
export const BASE_MAX_H = 1600;

export type EnrollStep = "models" | "detect" | "clean" | "save";

export interface EnrollOutcome {
  photo: Photo;
  faceFound: boolean;
}

export async function enrollPhoto(
  memberId: string,
  file: Blob,
  onStep: (s: EnrollStep) => void,
  onModel: LoadProgress,
): Promise<EnrollOutcome> {
  onStep("models");
  await loadVision(onModel);

  onStep("detect");
  const full = await blobToCanvas(file, 2400);
  const first = await analyze(full);
  const face = first.landmarks ? faceBox(first.landmarks) : null;
  const rect = portraitCrop(full.width, full.height, face);
  const base = crop(full, rect, BASE_MAX_W, BASE_MAX_H);

  onStep("clean");
  // 裁剪后重新识别，分割和关键点更准
  let analysis = await analyze(base);
  autoClean(base, { data: analysis.masks.faceSkin, w: analysis.masks.w, h: analysis.masks.h });
  analysis = await analyze(base);

  onStep("save");
  const yaw = analysis.landmarks ? estimateYaw(analysis.landmarks) : 1;
  const photo: Photo = {
    id: uid(),
    memberId,
    angle: angleOf(yaw, !!analysis.landmarks),
    yaw,
    base: await canvasToBlob(base, "image/jpeg", 0.92),
    width: base.width,
    height: base.height,
    analysis: packAnalysis(analysis),
    createdAt: Date.now(),
  };
  await put("photos", photo);
  return { photo, faceFound: !!analysis.landmarks };
}

export function angleOf(yaw: number, hasFace: boolean): Angle {
  if (!hasFace) return "side";
  const a = Math.abs(yaw);
  if (a < 0.22) return "front";
  if (a < 0.6) return "three-quarter";
  return "side";
}

export const ANGLE_LABEL: Record<Angle, string> = {
  front: "正脸",
  "three-quarter": "斜侧",
  side: "侧脸",
};
